import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

interface EmergencyContact {
  id: string;
  name: string;
  role: string;
  phone: string;
  sortOrder: number;
}

export function EmergencyContactsPanel() {
  const { user } = useAuth();
  const isOrganizer = user?.role === 'Organizer' || user?.role === 'Admin';

  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formRole, setFormRole] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const data = await apiClient<EmergencyContact[]>('/emergency-contacts');
      setContacts(data);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  async function handleAdd() {
    if (!formName.trim() || !formRole.trim() || !formPhone.trim()) return;
    setSaving(true);
    try {
      await apiClient('/emergency-contacts', {
        method: 'POST',
        body: { name: formName.trim(), role: formRole.trim(), phone: formPhone.trim() },
      });
      setFormName('');
      setFormRole('');
      setFormPhone('');
      setShowForm(false);
      fetchContacts();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this contact?')) return;
    try {
      await apiClient(`/emergency-contacts/${id}`, { method: 'DELETE' });
      fetchContacts();
    } catch {
      // silently fail
    }
  }

  if (loading) {
    return <div style={containerStyle}><p style={loadingStyle}>Loading contacts...</p></div>;
  }

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>🚨 Emergency Contacts</h2>

      {contacts.length === 0 && (
        <p style={emptyStyle}>No emergency contacts added yet.</p>
      )}

      <div style={listStyle}>
        {contacts.map((c) => (
          <div key={c.id} style={contactCardStyle}>
            <div style={contactInfoStyle}>
              <div style={contactNameStyle}>{c.name}</div>
              <div style={contactRoleStyle}>{c.role}</div>
              <a href={`tel:${c.phone}`} style={phoneStyle}>
                📞 {c.phone}
              </a>
            </div>
            {isOrganizer && (
              <button
                onClick={() => handleDelete(c.id)}
                style={deleteBtnStyle}
                aria-label={`Delete ${c.name}`}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {isOrganizer && !showForm && (
        <button onClick={() => setShowForm(true)} style={addBtnStyle}>
          + Add Contact
        </button>
      )}

      {isOrganizer && showForm && (
        <div style={formStyle}>
          <input
            type="text"
            placeholder="Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Role (e.g., Venue Manager)"
            value={formRole}
            onChange={(e) => setFormRole(e.target.value)}
            style={inputStyle}
          />
          <input
            type="tel"
            placeholder="Phone"
            value={formPhone}
            onChange={(e) => setFormPhone(e.target.value)}
            style={inputStyle}
          />
          <div style={formActionsStyle}>
            <button onClick={handleAdd} disabled={saving} style={saveBtnStyle}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} style={cancelBtnStyle}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Styles ---

const containerStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '80px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
  color: '#1a1a1a',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
  padding: '32px 0',
};

const emptyStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
  padding: '24px 0',
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const contactCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px',
  backgroundColor: '#f9fafb',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
};

const contactInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const contactNameStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#1f2937',
};

const contactRoleStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#6b7280',
};

const phoneStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#2563eb',
  textDecoration: 'none',
  fontWeight: 500,
  marginTop: '4px',
};

const deleteBtnStyle: React.CSSProperties = {
  minWidth: '36px',
  minHeight: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  color: '#dc2626',
  fontSize: '16px',
  cursor: 'pointer',
};

const addBtnStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '48px',
  padding: '12px 24px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#16a34a',
  border: 'none',
  borderRadius: '10px',
  cursor: 'pointer',
  marginTop: '8px',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '16px',
  backgroundColor: '#f0fdf4',
  borderRadius: '10px',
  border: '1px solid #bbf7d0',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: '15px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  boxSizing: 'border-box',
};

const formActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '10px',
};

const saveBtnStyle: React.CSSProperties = {
  flex: 1,
  minHeight: '44px',
  padding: '10px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#16a34a',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  flex: 1,
  minHeight: '44px',
  padding: '10px',
  fontSize: '15px',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: '#e5e7eb',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};
